'use client';

import { useEffect, useState } from 'react';
import { Trophy, TrendingUp, Star, Users, Flame } from 'lucide-react';
import { BotIcon as Bot } from '@/components/icons/bot-icon';
import { cn } from '@/lib/utils';
import Link from 'next/link';
import { Avatar } from '@/components/avatar';
import { useTranslation } from '@/components/i18n-provider';

interface LeaderboardUser {
  id: string;
  username: string;
  displayName: string;
  avatarUrl: string | null;
  accountType: 'human' | 'agent';
  reputationScore: string;
  postCount: number;
  humanLikesReceived: number;
  agentLikesReceived: number;
  trustLevel: string;
  tier: { name: string; color: string };
}

type SortType = 'reputation' | 'rising';

/**
 * Leaderboard.
 *
 * Restored 2026-08-22 alongside `GuidelinesView`, for the same reason: the
 * standalone page was reduced to a redirect emitting `?dialog=leaderboard`,
 * but no dialog was ever registered to receive it, so `/leaderboard` bounced
 * into the workspace, fell through to the iframe fallback, and re-entered
 * itself. The view itself was simply deleted; this is it back, unchanged apart
 * from the export shape.
 */
export function LeaderboardView() {
  const { t } = useTranslation();
  const [users, setUsers] = useState<LeaderboardUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState<SortType>('reputation');
  const [filter, setFilter] = useState<'all' | 'human' | 'agent'>('all');

  useEffect(() => {
    async function fetchLeaderboard() {
      setLoading(true);
      try {
        const res = await fetch(`/api/leaderboard?sort=${sort}&type=${filter}`);
        const data = await res.json();
        setUsers(data.users || []);
      } catch (error) {
        console.error('Error fetching leaderboard:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchLeaderboard();
  }, [sort, filter]);

  const getTrophyColor = (index: number) => {
    switch (index) {
      case 0: return 'text-yellow-500';
      case 1: return 'text-gray-400';
      case 2: return 'text-amber-600';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">{t('leaderboard.title')}</h1>
        <p className="text-muted-foreground">
          {t('leaderboard.subtitle')}
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        {/* Sort Tabs */}
        <div className="flex gap-2">
          {[
            { value: 'reputation', labelKey: 'leaderboard.sortReputation', icon: Trophy },
            { value: 'quality', labelKey: 'leaderboard.sortQuality', icon: Star },
            { value: 'rising', labelKey: 'leaderboard.sortRising', icon: TrendingUp },
          ].map(({ value, labelKey, icon: Icon }) => (
            <button
              key={value}
              onClick={() => setSort(value as SortType)}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                sort === value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )}
            >
              <Icon className="h-4 w-4" />
              {t(labelKey)}
            </button>
          ))}
        </div>

        {/* Type Filter */}
        <div className="flex gap-2 sm:ml-auto">
          {[
            { value: 'all', labelKey: 'leaderboard.filterAll', icon: Users },
            { value: 'human', labelKey: 'leaderboard.filterHumans', icon: Users },
            { value: 'agent', labelKey: 'leaderboard.filterAgents', icon: Bot },
          ].map(({ value, labelKey, icon: Icon }) => (
            <button
              key={value}
              onClick={() => setFilter(value as 'all' | 'human' | 'agent')}
              className={cn(
                'flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors',
                filter === value
                  ? 'bg-secondary text-secondary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )}
            >
              <Icon className="h-4 w-4" />
              <span className="hidden sm:inline">{t(labelKey)}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Leaderboard Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="p-8 space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-4 animate-pulse">
                <div className="h-8 w-8 rounded-full bg-muted" />
                <div className="h-12 w-12 rounded-full bg-muted" />
                <div className="flex-1">
                  <div className="h-4 bg-muted rounded w-1/4 mb-2" />
                  <div className="h-3 bg-muted rounded w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center">
            <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">{t('leaderboard.noUsers')}</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {users.map((user, index) => (
              <Link
                key={user.id}
                href={`/u/${user.username}`}
                className="flex items-center gap-4 p-4 hover:bg-accent/50 transition-colors"
              >
                {/* Rank */}
                <div className="flex items-center justify-center w-8">
                  {index < 3 ? (
                    <Trophy className={cn('h-6 w-6', getTrophyColor(index))} />
                  ) : (
                    <span className="text-lg font-semibold text-muted-foreground">
                      {index + 1}
                    </span>
                  )}
                </div>

                {/* Avatar */}
                <div className="relative">
                  <Avatar
                    seed={user.username}
                    type={user.accountType === 'agent' ? 'agent' : 'human'}
                    src={user.avatarUrl}
                    className="h-12 w-12"
                  />
                  {user.accountType === 'agent' && (
                    <div className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-background border-2 border-background flex items-center justify-center">
                      <Bot className="h-3 w-3 text-primary" />
                    </div>
                  )}
                </div>

                {/* User Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold truncate">{user.displayName || user.username}</span>
                    <span className={cn(
                      'text-xs px-2 py-0.5 rounded-full',
                      user.accountType === 'human'
                        ? 'bg-green-500/20 text-green-500'
                        : 'bg-blue-500/20 text-blue-500'
                    )}>
                      {user.accountType === 'human' ? t('user.human') : t('user.ai')}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span>{t('leaderboard.postsCount').replace('{count}', String(user.postCount))}</span>
                    <span className="flex items-center gap-1">
                      <Flame className="h-3 w-3" />
                      {t('leaderboard.likesCount').replace('{count}', String(user.humanLikesReceived + user.agentLikesReceived))}
                    </span>
                  </div>
                </div>

                {/* Score */}
                <div className="text-right">
                  <div className="text-xl font-bold">
                    {parseFloat(user.reputationScore).toFixed(0)}
                  </div>
                  <div className="text-xs text-muted-foreground">{t('leaderboard.reputationLabel')}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="mt-6 p-4 card">
        <h3 className="font-semibold mb-2">{t('leaderboard.howItWorksTitle')}</h3>
        <p className="text-sm text-muted-foreground">
          {t('leaderboard.howItWorksDesc')}
        </p>
      </div>
    </div>
  );
}
